import React, { useState } from "react";
import moment from "moment";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

/** Large day cells for touch / KDS (matches FulfillmentTab calendar sizing). */
export const touchDatePickerSx = {
  width: "100%",
  border: "none",
  "& .MuiPickersLayout-root": {
    border: "none",
  },
  "& .MuiDateCalendar-root": {
    width: "100%",
    maxWidth: "100%",
    height: "auto",
    maxHeight: "none",
  },
  "& .MuiDayCalendar-slideTransition": {
    minHeight: 340,
    height: "auto",
    overflow: "visible",
  },
  "& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer": {
    width: "100%",
    justifyContent: "space-around",
  },
  "& .MuiDayCalendar-weekDayLabel": {
    width: 48,
    height: 40,
    fontSize: "0.95rem",
    margin: 0,
  },
  "& .MuiPickersDay-root": {
    width: 48,
    height: 48,
    fontSize: "1.15rem",
    margin: "2px",
  },
};

export default function BTouchDatePicker(props) {
  const {
    value,
    onChange,
    label,
    required,
    fullWidth,
    hideIcon,
    inline,
    inlineLabel,
    inlineActionLabel,
    hideInlineIcon,
    inlineVariant = "subtitle1",
    variant = "outlined",
    sx,
    slotProps,
    ...pickerProps
  } = props;

  const [open, setOpen] = useState(false);

  const displayValue =
    value && moment(value, "YYYY-MM-DD", true).isValid()
      ? moment(value, "YYYY-MM-DD").format("MMM D, YYYY")
      : "";

  const textFieldSlot = slotProps?.textField || {};

  const releaseFocus = () => {
    const el = document.activeElement;
    if (el && typeof el.blur === "function") {
      el.blur();
    }
  };

  const openDialog = () => setOpen(true);

  const closeDialog = () => {
    setOpen(false);
    releaseFocus();
  };

  const handleSelect = (dateValue) => {
    onChange((dateValue && moment(dateValue).format("YYYY-MM-DD")) || "");
    closeDialog();
  };

  const triggerLabel = inlineLabel || displayValue;

  return (
    <>
      {inline ? (
        <Box
          component="button"
          type="button"
          aria-haspopup="dialog"
          aria-label={label ? `Change ${label}` : "Change date"}
          onClick={openDialog}
          sx={{
            display: "inline-flex",
            alignItems: inlineActionLabel ? "baseline" : "center",
            flexWrap: "wrap",
            gap: inlineActionLabel ? 1 : 0.75,
            border: "none",
            background: "none",
            cursor: "pointer",
            p: 0,
            m: 0,
            textAlign: "left",
            color: inlineVariant === "h2" ? "text.primary" : "primary.main",
            "&:hover .BTouchDatePicker-inlineLabel": {
              textDecoration: inlineVariant === "h2" ? "underline" : "underline",
            },
          }}
        >
          {!hideInlineIcon && (
            <CalendarMonthOutlinedIcon
              sx={{ fontSize: inlineVariant === "h2" ? "1.75rem" : "1.25rem", flexShrink: 0 }}
            />
          )}
          <Typography
            component="span"
            variant={inlineVariant}
            className="BTouchDatePicker-inlineLabel"
            sx={{
              color: "inherit",
              fontWeight: inlineVariant === "h2" ? undefined : 500,
              textDecoration: inlineVariant === "h2" ? "underline" : "none",
              ...(inlineVariant === "h2" ? { m: 0, lineHeight: 1.2 } : {}),
            }}
          >
            {triggerLabel}
          </Typography>
          {inlineActionLabel && (
            <Typography
              component="span"
              variant="body2"
              className="BTouchDatePicker-inlineAction"
              sx={{
                color: "primary.main",
                textDecoration: "none",
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {inlineActionLabel}
            </Typography>
          )}
        </Box>
      ) : (
        <TextField
          {...textFieldSlot}
          label={label ?? textFieldSlot.label}
          value={displayValue}
          required={required}
          fullWidth={fullWidth !== false}
          variant={variant ?? textFieldSlot.variant}
          tabIndex={0}
          role="button"
          aria-haspopup="dialog"
          onMouseDown={(e) => {
            e.preventDefault();
            openDialog();
          }}
          onKeyDown={(e) => {
            textFieldSlot.onKeyDown?.(e);
            if (e.defaultPrevented) {
              return;
            }
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openDialog();
            }
          }}
          sx={{
            cursor: "pointer",
            "& input": {
              cursor: "pointer",
              caretColor: "transparent",
              userSelect: "none",
              pointerEvents: "none",
            },
            ...textFieldSlot.sx,
          }}
          InputProps={{
            readOnly: true,
            ...textFieldSlot.InputProps,
            sx: { cursor: "pointer", ...textFieldSlot.InputProps?.sx },
            endAdornment: (
              <InputAdornment position="end">
                {!hideIcon && <CalendarMonthOutlinedIcon />}
              </InputAdornment>
            ),
          }}
          inputProps={{
            ...textFieldSlot.inputProps,
            readOnly: true,
            tabIndex: -1,
          }}
        />
      )}
      <Dialog
        open={open}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { mx: 1, width: "calc(100% - 16px)", maxWidth: 480 },
        }}
      >
        <DialogContent sx={{ pt: 2, pb: 1, px: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDatePicker
              {...pickerProps}
              sx={{ ...touchDatePickerSx, ...sx }}
              slotProps={{
                actionBar: {
                  actions: [],
                },
                toolbar: { hidden: true },
                ...slotProps,
              }}
              views={["day"]}
              value={value ? moment(value, "YYYY-MM-DD").toDate() : null}
              onChange={handleSelect}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button fullWidth variant="text" color="secondary" onClick={closeDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
